import { Play, Pause, Download } from 'lucide-react';
import { useRef, useState, useEffect } from 'react';

/* ---------- helper ---------- */
const formatRelativeDate = (ts) => {
    if (!ts) return '';
    const created = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    const now = new Date();
    const diffMs = now - created;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    
    // Less than a minute
    if (diffSeconds < 60) return `${diffSeconds} seconds ago`;
    
    // Less than an hour
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    
    // Less than a day (but more than an hour)
    if (diffHours < 24) return `${diffHours} hours ago`;
    
    // For days and beyond, use the existing logic
    const today = new Date(); 
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor(
      (today - new Date(created.getFullYear(), created.getMonth(), created.getDate())) /
      86_400_000
    );
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return 'Last week';
    return `${Math.floor(diffDays / 7)} weeks ago`;
  };

const HistoryItem = ({ item, onPlay }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const audioUrl = item.audioFileUrl || item.audioUrl || '';

  /* tidy-up on unmount */
  useEffect(() => () => {
    audioRef.current?.pause();
    if (audioRef.current) audioRef.current.currentTime = 0;
  }, []);

  const togglePlay = () => {
    if (!audioUrl) return;
    if (playing) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
      onPlay?.(null);
    } else {
      audioRef.current.play().catch(console.error);
      setPlaying(true);
      onPlay?.(item.id);
    }
  };

  const download = () => {
    if (!audioUrl) return;
    const a = document.createElement('a');
    a.href = audioUrl;
    a.download = `audio-${item.id}.mp3`;
    a.click();
  };

  return (
    <div className="group relative hover:bg-gray-50 px-2 py-2 rounded-lg transition-colors">
      {/* title + date (date is below) */}
      <div
        className={`transition-all ${playing ? 'pr-16' : 'group-hover:pr-16'}`}
      >
        <h3 className="text-sm text-gray-900 font-medium truncate">{item.text}</h3>
        <span className="text-xs text-gray-400 mt-0.5 block">
          {formatRelativeDate(item.createdAt)}
        </span>
      </div>

      {/* controls */}
      <div
        className={`absolute right-3 top-1/2 -translate-y-1/2 z-10 flex items-center gap-2 transition-opacity ${
          playing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
      >
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="auto"
          onEnded={() => {
            setPlaying(false);
            onPlay?.(null);
          }}
          className="hidden"
        />

        <button
          onClick={togglePlay}
          className={`w-8 h-8 rounded-full flex items-center justify-center ${
            audioUrl
              ? 'bg-gray-100 hover:bg-gray-200'
              : 'bg-gray-100 opacity-50 cursor-not-allowed'
          }`}
        >
          {playing ? <Pause size={14} /> : <Play size={14} className="ml-[2px]" />}
        </button>

        <button
          onClick={download}
          disabled={!audioUrl}
          className={`p-1 rounded ${
            audioUrl ? 'hover:bg-gray-200' : 'opacity-50 cursor-not-allowed'
          }`}
        >
          <Download size={16} />
        </button>
      </div>
    </div>
  );
};

export default HistoryItem;
